import { useDeleteAssets, useGetPaginateAssets } from '../../api-http/assets';
import { DialogModal } from '../../components';
import {
  BulkSelectionBar,
  managementTableSx,
} from '../../components/ManagementPageLayout';
import { AlertBar } from '../../components/AlertBar';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../components/TableTheme';
import { notifyMsg } from '../../configs';
import { useAuth } from '../../context/auth-context';
import { AssetFileType } from '../../types/assets.types';
import { getAssetDisplayName } from '../../util/assetDisplayName';
import { OrderEnum, SortEnum } from '../../types/pagination.types';
import { UserRole } from '../../types/user.types';
import { displayDate } from '../../util/dateTime';
import { getStaticFileUrl } from '../../util/getStaticFileUrl';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useSnack } from '../../util/useSnack';
import { useTable } from '../../util/useTable';
import { AssetWithPlaceholder } from './AssetWithPlaceholder';
import {
  Checkbox,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  TablePagination,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { styled } from '@mui/system';
import { useConfirm } from 'material-ui-confirm';
import * as React from 'react';
import { FC, useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Copy, Download, Eye, Trash2 } from 'react-feather';

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

const AssetsPreview = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 100%;
`;

const StyledImage = styled('img')`
  max-width: 100%;
  width: 800px;
  height: auto;
`;

const StyledVideo = styled('video')`
  max-width: 100%;
  width: 800px;
  height: auto;
`;

interface AssetsTableProp {
  refresh: boolean;
  assetSearchTxt: string;
  showImage: boolean;
  showVideo: boolean;
}

export const AssetsTable: FC<AssetsTableProp> = ({
  refresh,
  assetSearchTxt,
  showImage,
  showVideo,
}) => {
  const confirm = useConfirm();
  const disableDelete = useIsUserRole([UserRole.user, UserRole.dataEditor]);
  let fileType: AssetFileType;

  if (showImage && showVideo) {
    fileType = AssetFileType.All;
  } else if (showImage) {
    fileType = AssetFileType.Image;
  } else if (showVideo) {
    fileType = AssetFileType.Video;
  } else {
    fileType = AssetFileType.None;
  }

  const { switchedOrgId } = useAuth();
  const { showErrSnack, showSuccessSnack } = useSnack();
  const { sortOnClick, pagingParams, tableProps } = useTable();
  const { sortby, order } = pagingParams;

  const {
    data: list,
    error: listError,
    isLoading: listIsLoading,
    refetch: listRefetch,
  } = useGetPaginateAssets({
    ...pagingParams,
    fileType,
    search: assetSearchTxt,
  });

  const {
    error,
    mutateAsync: deleteAsset,
    data: deletedAsset,
    isLoading,
  } = useDeleteAssets({
    onError: () => showErrSnack(notifyMsg.DELETE_ATTEMPT_FAILED),
  });

  const [assetPreviewModal, setAssetPreviewModal] = useState(false);
  const [imageUrl, setImageUrl] = useState();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const file = (imageUrl: string | undefined) => {
    if (imageUrl) {
      const fileType = imageUrl.split('.').pop()?.toLowerCase();
      if (
        (fileType && fileType.endsWith('jpeg')) ||
        fileType?.endsWith('jpg') ||
        fileType?.endsWith('png') ||
        fileType?.endsWith('gif')
      ) {
        return <StyledImage src={imageUrl} alt="Image Preview" />;
      } else if (fileType && fileType.endsWith('mp4')) {
        return <StyledVideo src={imageUrl} autoPlay controls />;
      }
    }
    return <div>Invalid media URL</div>;
  };

  useEffect(() => {
    listRefetch();
    setSelectedIds(new Set());
  }, [deletedAsset, refresh, switchedOrgId]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [assetSearchTxt, showImage, showVideo, pagingParams.page]);

  const handleDelete = async (id: string) => {
    try {
      await confirm({
        description: notifyMsg.DELETE_CONFIRMATION,
        confirmationText: 'Yes',
      });
      await deleteAsset({ id });
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      await listRefetch();
      showSuccessSnack(notifyMsg.ASSET_DELETE_SUCCESS);
    } catch (e) {}
  };

  const assets = list?.data || [];
  const deletableIds = assets
    .filter((asset) => asset.slides?.length === 0)
    .map((asset) => asset._id);
  const allSelected =
    deletableIds.length > 0 && deletableIds.every((id) => selectedIds.has(id));
  const someSelected = deletableIds.some((id) => selectedIds.has(id));

  const setSelected = (id: string, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(deletableIds) : new Set());
  };

  const removeSelected = async () => {
    if (!selectedIds.size) return;
    try {
      await confirm({
        title: `Delete ${selectedIds.size} assets?`,
        description:
          'The selected unused assets will be permanently deleted. This action cannot be undone.',
        confirmationText: 'Delete selected',
      });
      for (const id of Array.from(selectedIds)) {
        await deleteAsset({ id });
      }
      setSelectedIds(new Set());
      await listRefetch();
      showSuccessSnack('Selected assets deleted');
    } catch (e) {}
  };

  return (
    <>
      <AlertBar severity="error" msg={error?.msg || listError?.msg} />
      {(isLoading || listIsLoading) && <LinearProgress />}
      <BulkSelectionBar
        count={selectedIds.size}
        singularLabel="asset"
        pluralLabel="assets"
        onClear={() => setSelectedIds(new Set())}
        onDelete={removeSelected}
        disabled={disableDelete || isLoading}
      />
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={(event) => toggleAll(event.target.checked)}
                  disabled={disableDelete || !deletableIds.length}
                  inputProps={{ 'aria-label': 'Select all unused assets' }}
                />
              </StyledTableCell>
              <StyledTableCell width={200}>
                <StyledTableSortLabel
                  active={sortby === SortEnum.fileName || true}
                  direction={
                    sortby === SortEnum.fileName ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.fileName)}
                >
                  Asset name
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align={'center'} width={110}>
                Asset type
              </StyledTableCell>
              <StyledTableCell align={'center'}>Preview</StyledTableCell>
              <StyledTableCell align={'center'} width={150}>
                <StyledTableSortLabel
                  active={sortby === SortEnum.createdAt || true}
                  direction={
                    sortby === SortEnum.createdAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.createdAt)}
                >
                  Created
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align={'center'} width={110}>
                Created by
              </StyledTableCell>
              <StyledTableCell align={'center'} width={110}>
                Usage
              </StyledTableCell>
              <StyledTableCell align={'center'} width={190}>
                Actions
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((asset) => {
              const displayName = getAssetDisplayName(asset);
              return (
                <TableRow
                  key={asset?._id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(asset._id)}
                      onChange={(event) =>
                        setSelected(asset._id, event.target.checked)
                      }
                      disabled={disableDelete || asset.slides?.length !== 0}
                      inputProps={{
                        'aria-label': `Select ${displayName}`,
                      }}
                    />
                  </TableCell>
                  <TableCell component={'th'} scope={'row'}>
                    {displayName}
                  </TableCell>
                  <TableCell align={'center'}>
                    <Chip
                      size="small"
                      label={
                        asset?.fileType.includes('video') ? 'Video' : 'Image'
                      }
                      sx={{
                        minWidth: 72,
                        bgcolor: asset?.fileType.includes('video')
                          ? '#f3e8ff'
                          : '#dcfce7',
                        color: asset?.fileType.includes('video')
                          ? '#7e22ce'
                          : '#15803d',
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ display: 'flex', justifyContent: 'center' }}>
                    {asset?.fileType.includes('image') && (
                      <AssetWithPlaceholder
                        src={getStaticFileUrl(asset?.thumbnailUrl)}
                        fileName={displayName}
                        width="140px"
                        type="image"
                      />
                    )}
                    {asset?.fileType.includes('video') && (
                      <AssetWithPlaceholder
                        src={getStaticFileUrl(asset?.thumbnailUrl || '')}
                        fileName={displayName}
                        width="140px"
                        type="video"
                      />
                    )}
                  </TableCell>
                  <TableCell align={'center'}>
                    {displayDate(asset?.createdAt)}
                  </TableCell>
                  <TableCell align={'center'}>
                    {asset?.createdBy?.name}
                  </TableCell>
                  <TableCell align={'center'}>
                    <Chip
                      size="small"
                      label={
                        asset?.slides?.length === 0 ? 'Not used' : 'In use'
                      }
                      sx={{
                        bgcolor:
                          asset?.slides?.length === 0 ? '#f1f5f9' : '#dcfce7',
                        color:
                          asset?.slides?.length === 0 ? '#475569' : '#15803d',
                        fontWeight: 700,
                      }}
                    />
                  </TableCell>
                  <TableCell align={'center'}>
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={0.75}
                    >
                      <Tooltip title="Preview asset">
                        <IconButton
                          aria-label={`Preview ${displayName}`}
                          onClick={() => {
                            setImageUrl(asset?.url);
                            setAssetPreviewModal(true);
                          }}
                          sx={{ ...actionButtonSx, color: '#0b8fca' }}
                        >
                          <Eye size={15} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download asset">
                        <IconButton
                          component="a"
                          href={asset?.url}
                          download
                          aria-label={`Download ${displayName}`}
                          sx={{ ...actionButtonSx, color: '#475569' }}
                        >
                          <Download size={15} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy asset URL">
                        <span>
                          <CopyToClipboard
                            text={asset?.url}
                            onCopy={() =>
                              showSuccessSnack(
                                notifyMsg.CLIP_BOARD_COPIED_SUCCESS,
                              )
                            }
                          >
                            <IconButton
                              aria-label={`Copy URL for ${displayName}`}
                              sx={{ ...actionButtonSx, color: '#64748b' }}
                            >
                              <Copy size={15} />
                            </IconButton>
                          </CopyToClipboard>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          asset.slides?.length
                            ? 'Used in slides'
                            : 'Delete asset'
                        }
                      >
                        <span>
                          <IconButton
                            aria-label={`Delete ${displayName}`}
                            disabled={
                              disableDelete || asset.slides?.length !== 0
                            }
                            onClick={() => handleDelete(asset._id)}
                            sx={{
                              ...actionButtonSx,
                              color: '#dc2626',
                              bgcolor: '#fffafa',
                              borderColor: '#fecaca',
                            }}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={list?.pagination.total === 0}
          count={list?.pagination.total || 0}
        />
      </TableContainer>

      <DialogModal
        title="Asset preview"
        onClose={() => setAssetPreviewModal(false)}
        open={assetPreviewModal}
        width={'800px'}
      >
        <AssetsPreview>{file(imageUrl)}</AssetsPreview>
      </DialogModal>
    </>
  );
};
